import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, Package, Truck, CheckCircle, Clock, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";

interface OrderStatus {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  status: string;
  created_at: string;
  total_amount: number;
  items_count: number;
}

const statusOrder = ["pending", "confirmed", "processing", "shipped", "delivered"];
const statusIcons = { pending: Clock, confirmed: CheckCircle, processing: Package, shipped: Truck, delivered: Package };
const statusColors: Record<string, { color: string; bgColor: string }> = {
  pending: { color: "text-yellow-500", bgColor: "bg-yellow-500/10" },
  confirmed: { color: "text-blue-500", bgColor: "bg-blue-500/10" },
  processing: { color: "text-purple-500", bgColor: "bg-purple-500/10" },
  shipped: { color: "text-indigo-500", bgColor: "bg-indigo-500/10" },
  delivered: { color: "text-green-500", bgColor: "bg-green-500/10" },
};

const formatDate = (dateString: string) => new Date(dateString).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });

const OrderCard = ({ order, index }: { order: OrderStatus; index: number }) => {
  const [expanded, setExpanded] = useState(index === 0);
  const { t } = useLanguage();
  const status = order.status as keyof typeof statusIcons;
  const Icon = statusIcons[status] || Clock;
  const colors = statusColors[status] || statusColors.pending;
  const statusT = t.track.status[status as keyof typeof t.track.status] || t.track.status.pending;
  const currentStatusIndex = statusOrder.indexOf(order.status);

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.08 }} className="bg-card border border-border rounded-sm overflow-hidden">
      <button onClick={() => setExpanded(!expanded)} className="w-full flex items-center justify-between p-5 md:p-6 hover:bg-muted/20 transition-colors text-left">
        <div className="flex items-center gap-4 min-w-0">
          <div className={`${colors.bgColor} p-2.5 rounded-lg flex-shrink-0`}><Icon className={`w-5 h-5 ${colors.color}`} /></div>
          <div className="min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <p className="font-serif text-lg truncate">{order.customer_name}</p>
              <span className={`px-2.5 py-0.5 rounded-full text-[11px] font-medium tracking-wider uppercase ${colors.bgColor} ${colors.color}`}>{statusT.label}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-0.5">
              {formatDate(order.created_at)} · {order.items_count} {order.items_count !== 1 ? t.track.items : t.track.item} · {order.total_amount.toFixed(2)} {t.common.egp}
            </p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-3" /> : <ChevronDown className="w-5 h-5 text-muted-foreground flex-shrink-0 ml-3" />}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.25 }} className="overflow-hidden">
            <div className="px-5 md:px-6 pb-6 space-y-6 border-t border-border/50 pt-5">
              <div className="grid grid-cols-2 gap-4">
                <div><p className="text-[10px] text-primary tracking-[0.2em] uppercase mb-1 font-medium">{t.track.email}</p><p className="text-sm text-muted-foreground break-all">{order.customer_email}</p></div>
                <div><p className="text-[10px] text-primary tracking-[0.2em] uppercase mb-1 font-medium">{t.track.phone}</p><p className="text-sm text-muted-foreground">{order.customer_phone || "N/A"}</p></div>
              </div>
              <div>
                <p className="text-[10px] text-primary tracking-[0.2em] uppercase mb-4 font-medium">{t.track.orderJourney}</p>
                <div className="flex items-center justify-between gap-1">
                  {statusOrder.map((stage, i) => {
                    const StageIcon = statusIcons[stage as keyof typeof statusIcons] || Clock;
                    const stageColors = statusColors[stage] || statusColors.pending;
                    const stageT = t.track.status[stage as keyof typeof t.track.status] || t.track.status.pending;
                    const isCompleted = i <= currentStatusIndex;
                    const isCurrent = stage === order.status;
                    return (
                      <div key={stage} className="flex items-center flex-1 last:flex-initial">
                        <div className="flex flex-col items-center">
                          <div className={`w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center border-2 transition-all ${isCompleted ? `${stageColors.bgColor} border-primary` : "border-border bg-muted/50"} ${isCurrent ? "ring-2 ring-primary/20 ring-offset-2 ring-offset-background" : ""}`}>
                            <StageIcon className={`w-4 h-4 ${isCompleted ? stageColors.color : "text-muted-foreground/50"}`} />
                          </div>
                          <p className={`text-[10px] mt-1.5 text-center hidden md:block ${isCompleted ? "text-foreground" : "text-muted-foreground/50"}`}>{stageT.label}</p>
                        </div>
                        {i < statusOrder.length - 1 && <div className={`flex-1 h-0.5 mx-1 ${i < currentStatusIndex ? "bg-primary" : "bg-border"}`} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const TrackOrder = () => {
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<OrderStatus[]>([]);
  const [searched, setSearched] = useState(false);
  const { toast } = useToast();
  const { t } = useLanguage();
  const { isAuthenticated } = useAuth();

  const fetchOrdersAuthenticated = async (searchValue: string) => {
    let query = supabase.from("orders").select("*").order("created_at", { ascending: false });
    if (searchValue.includes("@")) query = query.eq("customer_email", searchValue.toLowerCase());
    else query = query.eq("customer_phone", searchValue);
    const { data: foundOrders, error } = await query;
    if (error) throw error;
    if (!foundOrders || foundOrders.length === 0) return [];

    const orderIds = foundOrders.map((o) => o.id);
    const { data: items } = await supabase.from("order_items").select("order_id").in("order_id", orderIds);
    const itemCountMap: Record<string, number> = {};
    items?.forEach((item) => { itemCountMap[item.order_id] = (itemCountMap[item.order_id] || 0) + 1; });

    return foundOrders.map((o) => ({
      id: o.id, customer_name: o.customer_name, customer_email: o.customer_email,
      customer_phone: o.customer_phone || "", status: o.status || "pending",
      created_at: o.created_at, total_amount: o.total_amount, items_count: itemCountMap[o.id] || 0,
    }));
  };

  const fetchOrdersGuest = async (searchValue: string) => {
    const { data, error } = await supabase.functions.invoke("track-order", {
      body: { search_value: searchValue.trim() },
    });
    if (error) throw error;
    if (!data?.orders || data.orders.length === 0) return [];
    return data.orders.map((o: any) => ({
      id: o.id, customer_name: o.customer_name, customer_email: o.customer_email,
      customer_phone: o.customer_phone || "", status: o.status || "pending",
      created_at: o.created_at, total_amount: o.total_amount, items_count: o.items_count || 0,
    }));
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    setSearched(true);
    if (!searchInput.trim()) {
      toast({ title: t.common.error, description: t.track.errorEmpty, variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const results = isAuthenticated
        ? await fetchOrdersAuthenticated(searchInput)
        : await fetchOrdersGuest(searchInput);

      if (results.length === 0) {
        toast({ title: t.common.error, description: t.track.errorNotFound, variant: "destructive" });
      }
      setOrders(results);
    } catch (error: any) {
      console.error("Error fetching orders:", error);
      toast({ title: t.common.error, description: error.message || "Failed to fetch orders", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="py-24 bg-gradient-to-b from-background to-muted/20" id="track-order">
      <div className="container-luxury">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
          <span className="text-primary text-sm tracking-[0.3em] uppercase">{t.track.badge}</span>
          <h2 className="text-5xl md:text-6xl font-serif mt-4 mb-6">{t.track.title}</h2>
          <div className="divider-gold w-32 mx-auto" />
          <p className="text-muted-foreground mt-6 max-w-xl mx-auto">{t.track.description}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="max-w-2xl mx-auto mb-12">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-2 p-2 bg-card border border-border rounded-sm">
            <Input type="text" placeholder={t.track.placeholder} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} className="flex-1 border-0 bg-transparent focus:outline-none focus:ring-0" required />
            <Button type="submit" variant="bronze" disabled={loading} className="px-8 md:whitespace-nowrap">
              {loading ? (<><Search className="w-4 h-4 mr-2 animate-spin" />{t.track.searching}</>) : (<><Search className="w-4 h-4 mr-2" />{t.track.searchBtn}</>)}
            </Button>
          </form>
        </motion.div>

        {searched && orders.length > 0 && (
          <div className="max-w-2xl mx-auto space-y-3">
            <p className="text-sm text-muted-foreground mb-4">
              {t.track.found} <span className="text-foreground font-medium">{orders.length}</span> {orders.length !== 1 ? t.track.orders : t.track.order}
            </p>
            {orders.map((order, index) => <OrderCard key={order.id} order={order} index={index} />)}
          </div>
        )}

        {searched && orders.length === 0 && !loading && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-2xl mx-auto">
            <div className="bg-card border border-border rounded-sm p-8 text-center">
              <p className="text-lg text-muted-foreground">{t.track.noResult}</p>
              <Button variant="outline" onClick={() => { setSearched(false); setSearchInput(""); setOrders([]); }} className="mt-6">{t.track.searchAgain}</Button>
            </div>
          </motion.div>
        )}
      </div>
    </section>
  );
};

export default TrackOrder;
